import { computed, ref, nextTick } from "vue";
import { useStore } from "vuex";
import Helpers from "../../helpers";


export function useLonja() {
    const store = useStore();

    // Estado de UI
    const spinner = ref(true);
    const isLoading = ref(false);
    const alertTable = ref({ state: false, message: "", color: "success" });

    // Filtros
    const search = ref(null);
    const onlyActive = ref(false);

    // Modales
    const modalActiveForm = ref(false);
    const modalActiveDelete = ref(false);
    const modalActiveAddMember = ref(false);
    const selectedLonja = ref(null);

    // Constantes de timeout
    const ALERT_TIMEOUT = 5000;
    const SUCCESS_ALERT_TIMEOUT = 1500;

    const showAlert = (message, color = "success", timeout = ALERT_TIMEOUT) => {
        alertTable.value = { state: true, message, color };
        setTimeout(() => {
            alertTable.value.state = false;
        }, timeout);
    };

    const handleError = (error, defaultMessage = "Ha ocurrido un error") => {
        const msg =
            error?.response?.data?.message ||
            error?.message ||
            defaultMessage;
        showAlert(msg, "danger");
        console.error("Lonjas error:", error);
    };

    const dispatchWithErrorHandling = async (action, payload = null) => {
        try {
            return await store.dispatch(action, payload);
        } catch (error) {
            handleError(error);
            throw error;
        }
    };

    // ---------- Listado ----------
    const getLonjas = async (page = 1) => {
        if (page < 1) page = 1;

        const currentFilters = {
            search: search.value,
            active: onlyActive.value ? 1 : null,
        };
        const filterData = Helpers.eliminarNull(currentFilters);

        spinner.value = true;
        try {
            await dispatchWithErrorHandling("appLonjas/getLonjasAction", {
                filters: filterData,
                page,
            });
        } finally {
            spinner.value = false;
        }
    };

    const applyFilters = async () => {
        store.commit("appLonjas/setCurrentPage", 1);
        await getLonjas(1);
    };

    const cleanFilter = () => {
        search.value = null;
        onlyActive.value = false;
        store.commit("appLonjas/setCurrentPage", 1);
        nextTick(() => getLonjas(1));
    };

    const pageChangeHandle = async (value) => {
        const currentPage = store.state.appLonjas?.lonjas?.current_page || 1;
        let newPage = parseInt(currentPage);

        switch (value) {
            case "next":
                newPage = currentPage + 1;
                break;
            case "previous":
                newPage = Math.max(currentPage - 1, 1);
                break;
            default:
                newPage = parseInt(value) || 1;
                break;
        }

        if (newPage < 1) newPage = 1;
        store.commit("appLonjas/setCurrentPage", newPage);
        await getLonjas(newPage);
    };

    // ---------- Detalle ----------
    const getLonjaDetail = async (lonjaId) => {
        spinner.value = true;
        try {
            return await dispatchWithErrorHandling(
                "appLonjas/getLonjaDetailAction",
                lonjaId
            );
        } finally {
            spinner.value = false;
        }
    };

    // ---------- CRUD ----------
    const createLonja = async (data) => {
        isLoading.value = true;
        try {
            const result = await dispatchWithErrorHandling(
                "appLonjas/createLonjaAction",
                data
            );
            showAlert("Lonja creada exitosamente", "success", SUCCESS_ALERT_TIMEOUT);
            return { success: true, data: result };
        } catch (error) {
            return {
                success: false,
                errors: error?.response?.data?.errors || null,
            };
        } finally {
            isLoading.value = false;
        }
    };

    const updateLonja = async (lonjaId, data) => {
        isLoading.value = true;
        try {
            const result = await dispatchWithErrorHandling(
                "appLonjas/updateLonjaAction",
                { lonjaId, data }
            );
            showAlert(
                "Lonja actualizada exitosamente",
                "success",
                SUCCESS_ALERT_TIMEOUT
            );
            return { success: true, data: result };
        } catch (error) {
            return {
                success: false,
                errors: error?.response?.data?.errors || null,
            };
        } finally {
            isLoading.value = false;
        }
    };

    const deleteLonja = async (lonjaId) => {
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling("appLonjas/deleteLonjaAction", lonjaId);
            showAlert("Lonja eliminada", "success", SUCCESS_ALERT_TIMEOUT);
            return { success: true };
        } catch (error) {
            return { success: false };
        } finally {
            isLoading.value = false;
        }
    };

    const toggleActive = async (lonjaItem) => {
        if (!lonjaItem || !lonjaItem.id) return;
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling("appLonjas/toggleActiveAction", {
                lonjaId: lonjaItem.id,
                active: !lonjaItem.active,
            });
            showAlert("Estado actualizado", "success", SUCCESS_ALERT_TIMEOUT);
        } finally {
            isLoading.value = false;
        }
    };

    // ---------- Miembros ----------
    const addMember = async (lonjaId, userId) => {
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling("appLonjas/addMemberAction", {
                lonjaId,
                userId,
            });
            showAlert("Inmobiliaria agregada", "success", SUCCESS_ALERT_TIMEOUT);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                errors: error?.response?.data?.errors || null,
            };
        } finally {
            isLoading.value = false;
        }
    };

    const removeMember = async (lonjaId, userId) => {
        isLoading.value = true;
        try {
            await dispatchWithErrorHandling("appLonjas/removeMemberAction", {
                lonjaId,
                userId,
            });
            showAlert("Inmobiliaria removida", "success", SUCCESS_ALERT_TIMEOUT);
            return { success: true };
        } catch (error) {
            return { success: false };
        } finally {
            isLoading.value = false;
        }
    };

    const searchMainUsers = async (query = "", page = 1) => {
        try {
            return await store.dispatch("appLonjas/searchMainUsersAction", {
                query,
                page,
            });
        } catch (error) {
            handleError(error, "Error al buscar usuarios");
            return null;
        }
    };

    // ---------- Utilidades UI ----------
    const showModalForm = (lonjaItem = null) => {
        selectedLonja.value = lonjaItem ? { ...lonjaItem } : null;
        modalActiveForm.value = true;
    };

    const toggleModalForm = () => {
        modalActiveForm.value = false;
        selectedLonja.value = null;
    };

    const closeModalAndReload = async (message) => {
        if (message && typeof message === "string") {
            showAlert(message, "success", SUCCESS_ALERT_TIMEOUT);
        }
        toggleModalForm();
        await getLonjas(store.state.appLonjas?.lonjas?.current_page || 1);
    };

    const showModalDelete = (lonjaItem) => {
        selectedLonja.value = lonjaItem;
        modalActiveDelete.value = true;
    };

    const toggleModalDelete = () => {
        modalActiveDelete.value = false;
        selectedLonja.value = null;
    };

    const confirmDelete = async () => {
        if (!selectedLonja.value) return;
        const result = await deleteLonja(selectedLonja.value.id);
        toggleModalDelete();
        if (result.success) {
            await getLonjas(store.state.appLonjas?.lonjas?.current_page || 1);
        }
    };

    const showModalAddMember = () => {
        modalActiveAddMember.value = true;
    };

    const toggleModalAddMember = () => {
        modalActiveAddMember.value = false;
    };

    // Headers tabla
    const headers = Object.freeze([
        { field: "logo", label: "Logo" },
        { field: "name", label: "Nombre" },
        { field: "slug", label: "Slug" },
        { field: "inmobiliarias_count", label: "Inmobiliarias" },
        { field: "active", label: "Estado" },
        { field: "actions", label: "Acciones" },
    ]);

    // Computed
    const lonjas = computed(
        () =>
            store.state.appLonjas?.lonjas || {
                data: [],
                current_page: 1,
                last_page: 1,
                total: 0,
                per_page: 15,
            }
    );
    const lonjaDetail = computed(() => store.state.appLonjas?.lonjaDetail || null);

    return {
        // Estado
        spinner,
        isLoading,
        alertTable,
        search,
        onlyActive,
        modalActiveForm,
        modalActiveDelete,
        modalActiveAddMember,
        selectedLonja,
        headers,

        // Computed
        lonjas,
        lonjaDetail,

        // Listado
        getLonjas,
        applyFilters,
        cleanFilter,
        pageChangeHandle,

        // Detalle
        getLonjaDetail,

        // CRUD
        createLonja,
        updateLonja,
        deleteLonja,
        toggleActive,

        // Miembros
        addMember,
        removeMember,
        searchMainUsers,

        // UI
        showModalForm,
        toggleModalForm,
        closeModalAndReload,
        showModalDelete,
        toggleModalDelete,
        confirmDelete,
        showModalAddMember,
        toggleModalAddMember,
        showAlert,
    };
}
